import * as React from 'react'
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import { ListItemNode, ListNode } from '@lexical/list'
import { LinkNode } from '@lexical/link'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { HeadingNode } from '@lexical/rich-text'
import { CodeNode } from '@lexical/code'
import { tw } from 'utils/classnames'
import { $convertFromMarkdownString } from '@lexical/markdown'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  COMMAND_PRIORITY_HIGH,
  PASTE_COMMAND,
  $insertNodes,
  $createTextNode,
  TextNode,
} from 'lexical'

import { ToolbarPlugin } from './plugins/toolbar-plugin'
import { FloatingLinkEditorPlugin } from './plugins/floating-link-editor-plugin'
import {
  ALL_TRANSFORMERS,
  OnChangeHtmlPlugin,
} from './plugins/on-change-markdown-plugin/on-change-markdown-plugin'
import { EmojiNode } from './plugins/emoji-plugin/emoji-node'
import { NoFollowLinkNode } from './nodes/no-follow-link-node'

interface RichTextEditorProps {
  className?: string
  placeholder?: string
  placeholderClassName?: string
  onChange?: (value: string) => void
  initialValue?: string
  readonly?: boolean
  characterLimit?: number
  hideEmoji?: boolean
  hideLists?: boolean
  customToolbarClassName?: string
  customToolbarRight?: React.ReactNode
  selectPhotoArea?: React.ReactNode
  customIcons?: {
    bold?: React.ReactNode
    italic?: React.ReactNode
    underline?: React.ReactNode
  }
  autoFocus?: boolean
}

function PlainTextPastePlugin() {
  const [editor] = useLexicalComposerContext()

  React.useEffect(() => {
    const removePasteListener = editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        event.preventDefault()
        const plainText = event.clipboardData?.getData('text/plain')
        if (plainText) {
          editor.update(() => {
            const textNode = $createTextNode(plainText)
            $insertNodes([textNode])
          })
        }
        return true
      },
      COMMAND_PRIORITY_HIGH,
    )
    return () => {
      removePasteListener()
    }
  }, [editor])

  return null
}

function CharacterLimitPlugin({
  characterLimit,
  setCharacterLimitError,
}: {
  characterLimit: number
  setCharacterLimitError: (error: boolean) => void
}) {
  const [editor] = useLexicalComposerContext()

  React.useEffect(() => {
    return editor.registerNodeTransform(TextNode, node => {
      const text = node.getTextContent()
      const characterCount = text.length

      if (characterCount > characterLimit) {
        setCharacterLimitError(true)
        editor.update(() => {
          const previous = editor.getEditorState().read(() => node.getTextContent())
          if (text.length > previous.length) {
            node.setTextContent(previous)
          }
        })
      } else {
        setCharacterLimitError(false)
      }
    })
  }, [editor, characterLimit, setCharacterLimitError])

  return null
}

const theme = {
  link: 'text-cf-blue-700 underline',
  list: {
    olDepth: ['list-inside list-decimal'],
    ul: 'list-inside list-disc',
  },
  text: {
    bold: 'font-bold',
    italic: 'italic',
    underline: 'underline',
  },
}

function RichTextEditor({
  className,
  placeholder = 'Enter some text...',
  placeholderClassName,
  onChange,
  initialValue,
  readonly = false,
  characterLimit = 5000,
  hideEmoji = false,
  hideLists = false,
  customToolbarClassName,
  customToolbarRight,
  selectPhotoArea,
  customIcons,
  autoFocus = true,
}: RichTextEditorProps) {
  const [isLinkEditMode, setIsLinkEditMode] = React.useState(false)
  const [characterLimitError, setCharacterLimitError] = React.useState<boolean>(false)
  const [isEditorFocused, setIsEditorFocused] = React.useState(false)

  const initialConfig = {
    namespace: 'MyEditor',
    theme,
    onError: (e: Error) => {
      throw e
    },
    nodes: [
      ...(hideLists ? [] : [ListNode, ListItemNode]),
      NoFollowLinkNode,
      {
        replace: LinkNode,
        with: (node: LinkNode) => new NoFollowLinkNode(node.getURL()),
      },
      ...(hideEmoji ? [] : [EmojiNode]),
      HeadingNode,
      CodeNode,
    ],
    editable: !readonly,
    editorState: () =>
      $convertFromMarkdownString(initialValue || '', ALL_TRANSFORMERS, undefined, true),
  }

  const [floatingAnchorElem, setFloatingAnchorElem] = React.useState<HTMLDivElement | undefined>(
    undefined,
  )

  const onRef = (elem: HTMLDivElement) => {
    if (elem) {
      setFloatingAnchorElem(elem)
    }
  }

  return (
    <>
      <LexicalComposer initialConfig={initialConfig}>
        <PlainTextPastePlugin />
        <CharacterLimitPlugin
          characterLimit={characterLimit}
          setCharacterLimitError={setCharacterLimitError}
        />

        <div
          className={tw(
            !readonly &&
              'bg-cf-white border flex flex-col rounded-b-2 border-cf-transparent-grey-1000-12 rounded-2 relative w-full',
            isEditorFocused
              ? 'border-cf-transparent-grey-1000-52'
              : 'hover:border-cf-transparent-grey-1000-20',
            className,
          )}
        >
          <ToolbarPlugin
            setIsLinkEditMode={setIsLinkEditMode}
            hideEmoji={hideEmoji}
            hideLists={hideLists}
            customIcons={customIcons}
            customClassName={customToolbarClassName}
            rightSlot={customToolbarRight}
          />

          {!readonly ? (
            <FloatingLinkEditorPlugin
              anchorElem={floatingAnchorElem}
              isLinkEditMode={isLinkEditMode}
              setIsLinkEditMode={setIsLinkEditMode}
            />
          ) : null}

          <RichTextPlugin
            contentEditable={
              <div ref={onRef} className="flex flex-col h-[calc(100%-55px)] flex-1">
                <ContentEditable
                  onFocus={() => setIsEditorFocused(true)}
                  onBlur={() => setIsEditorFocused(false)}
                  className={tw(
                    'flex-1 h-full pt-2 border-t border-[#1313151F] overflow-y-auto max-h-36',
                    // oxlint-disable-next-line no-negated-condition
                    !readonly ? 'px-3 pb-1.5' : '',
                    'focus:outline-none focus:ring-0',
                  )}
                  maxLength={characterLimit}
                />
                {selectPhotoArea ? <div className="shrink-0">{selectPhotoArea}</div> : null}
              </div>
            }
            placeholder={
              <div
                className={tw(
                  'absolute left-[15px] pointer-events-none top-[66px] text-[#02091D] opacity-50',
                  placeholderClassName,
                )}
              >
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />

          <HistoryPlugin />
          {autoFocus ? <AutoFocusPlugin /> : null}

          {/* Hide list plugin if needed */}
          {!hideLists ? <ListPlugin /> : null}

          <LinkPlugin />
          <OnChangeHtmlPlugin onChange={onChange} initialValue={initialValue} />
        </div>
      </LexicalComposer>

      {/*  CHARACTER LIMIT ERROR */}
      {characterLimitError ? (
        <div className="text-16 mt-2 h-10 w-full bg-red-100 pt-2 text-center font-semibold text-[#b42d2d]">
          {`There is a maximum of ${characterLimit} characters for the context field`}
        </div>
      ) : null}
    </>
  )
}

export { RichTextEditor }
